import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import StructuredData from "../components/StructuredData";
import AdSense from "../components/AdSense";
import AffiliateSection from "../components/AffiliateSection";
import SponsoredBadge from "../components/SponsoredBadge";
import { ArrowLeft, ExternalLink, Calendar, Tag, Loader2, Share2, Twitter, Facebook, Linkedin, MessageCircle, Link as LinkIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/news/${id}`);
      setArticle(response.data);
    } catch (error) {
      console.error("Error fetching article:", error);
    } finally {
      setLoading(false);
    }
  };

  const getShareUrl = () => {
    return window.location.href;
  };

  const shareOnTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank');
  };

  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank');
  };

  const shareOnWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(article.title + ' ' + getShareUrl())}`;
    window.open(url, '_blank');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      console.error("Error copying link:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header onSearch={() => {}} />
        <div className="flex justify-center items-center py-20" data-testid="loading-indicator">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-900" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header onSearch={() => {}} />
        <div className="max-w-3xl mx-auto px-6 py-12">
          <p className="text-xl text-slate-600">Nenhuma notícia encontrada.</p>
        </div>
      </div>
    );
  }

  const publishedDate = format(new Date(article.published_date), "d 'de' MMMM 'de' yyyy", { locale: ptBR });

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title={article.title}
        description={article.rewritten_content.substring(0, 160)}
        image={article.image_url}
        article={true}
        publishedTime={article.published_date}
        author={article.source}
        type="article"
        keywords={`${article.category}, finance news, ${article.source}`}
      />
      <StructuredData article={article} />
      <Header onSearch={() => {}} />
      
      <article className="max-w-3xl mx-auto px-6 py-12" data-testid="article-detail">
        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-emerald-900 hover:text-emerald-700 mb-8 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Voltar para notícias</span>
        </button>

        {/* Article Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <span 
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest bg-emerald-900 text-white"
                data-testid="article-category"
              >
                <Tag className="w-3 h-3" />
                {article.category}
              </span>
              <span 
                className="flex items-center gap-1 text-sm text-slate-500"
                data-testid="article-date"
              >
                <Calendar className="w-4 h-4" />
                {publishedDate}
              </span>
            </div>

            {/* Share Button */}
            <div className="relative">
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-900 text-white rounded-full hover:bg-emerald-800 transition-colors"
                data-testid="share-button"
              >
                <Share2 className="w-4 h-4" />
                <span>Compartilhar</span>
              </button>

              {showShareMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 p-2 z-10">
                  <button
                    onClick={shareOnTwitter}
                    className="flex items-center gap-3 w-full px-4 py-2 hover:bg-slate-50 rounded-lg transition-colors"
                    data-testid="share-twitter"
                  >
                    <Twitter className="w-5 h-5 text-blue-400" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={shareOnFacebook}
                    className="flex items-center gap-3 w-full px-4 py-2 hover:bg-slate-50 rounded-lg transition-colors"
                    data-testid="share-facebook"
                  >
                    <Facebook className="w-5 h-5 text-blue-600" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={shareOnLinkedIn}
                    className="flex items-center gap-3 w-full px-4 py-2 hover:bg-slate-50 rounded-lg transition-colors"
                    data-testid="share-linkedin"
                  >
                    <Linkedin className="w-5 h-5 text-blue-700" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={shareOnWhatsApp}
                    className="flex items-center gap-3 w-full px-4 py-2 hover:bg-slate-50 rounded-lg transition-colors"
                    data-testid="share-whatsapp"
                  >
                    <MessageCircle className="w-5 h-5 text-green-600" />
                    <span>WhatsApp</span>
                  </button>
                  <button
                    onClick={copyLink}
                    className="flex items-center gap-3 w-full px-4 py-2 hover:bg-slate-50 rounded-lg transition-colors"
                    data-testid="copy-link"
                  >
                    <LinkIcon className="w-5 h-5 text-slate-600" />
                    <span>{linkCopied ? "Link copiado!" : "Copiar link"}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6"
            data-testid="article-title"
          >
            {article.title}
          </h1>
          
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Fonte:</span>
            <a
              href={article.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-emerald-900 hover:text-emerald-700 font-medium transition-colors"
              data-testid="article-source-link"
            >
              {article.source}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Article Image */}
        {article.image_url && (
          <div className="mb-8 rounded-xl overflow-hidden" data-testid="article-image">
            <img
              src={article.image_url}
              alt={article.title}
              loading="lazy"
              className="w-full h-auto object-cover"
            />
          </div>
        )}

        {/* Sponsored Badge (if sponsored) */}
        {article.sponsored && (
          <SponsoredBadge className="mb-8" />
        )}

        {/* Article Content */}
        <div 
          className="prose prose-lg max-w-none"
          data-testid="article-content"
        >
          <div className="text-base leading-relaxed text-slate-700 whitespace-pre-line">
            {article.rewritten_content}
          </div>
        </div>

        {/* Mid-Article Ad */}
        <div className="my-8">
          <AdSense 
            adSlot="0987654321"
            adFormat="rectangle"
          />
        </div>

        {/* Affiliate Section */}
        <AffiliateSection />

        {/* Source Attribution */}
        <div className="mt-12 p-6 bg-slate-100 rounded-xl border border-slate-200">
          <p className="text-sm text-slate-600">
            <strong>Nota:</strong> Este conteúdo foi reescrito e adaptado para fins informativos. Para a notícia original, visite{" "}
            <a
              href={article.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-900 hover:text-emerald-700 font-medium underline"
            >
              {article.source}
            </a>
            .
          </p>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default NewsDetail;